/** @jsxImportSource @emotion/react */
import { DetailsRow } from "../../../../components/DetailsRow";
import { formatOrderType } from "../../../../util";
import { Order } from "../../model/OrderSuccessResponse";
import { css } from "@emotion/react";
import { RejectOrderSelector } from "./RejectOrderSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  ButtonExternalLink,
  ButtonInternalLink,
} from "../../../../components/NewButtons";
import { CardContainer } from "../../../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../../../components/page-skeleton/PageHeader";
import { CardBody } from "../../../../components/page-skeleton/CardBody";
import { StyledInfoBox } from "../../../../components/StyledInfoBox";

interface OrderDetailsProps {
  order: Order | undefined;
  linkRoute: string;
}

const additionalCardBodyStyling = css`
  padding-bottom: 24px;
  gap: 16px;
`;

const displayOrderSource = (source: string | undefined) => {
  switch (source) {
    case "web":
      return "Web";
    case "mobile_ios":
      return "Mobile app (iOS)";
    case "mobile_android":
      return "Mobile app (Android)";
    case "venue_self_checkout":
      return "Kiosk";
    case "venue_mobile":
      return "Beacon box";
    default:
      return "External";
  }
};

export const OrderDetails = ({ order, linkRoute }: OrderDetailsProps) => {
  return (
    <CardContainer>
      <PageHeader title="Order details">
        {order?.trackingLink && order.deliveryType !== "none" && (
          <ButtonExternalLink
            width="fit-content"
            icon={<FontAwesomeIcon icon={solid("external-link")} />}
            href={order?.trackingLink}
            type="dark"
          >
            Track and trace
          </ButtonExternalLink>
        )}
        {order?.actions.includes("rejectable") && <RejectOrderSelector />}
        {order?.customer.id !== undefined && (
          <ButtonInternalLink to={linkRoute}>
            Go to user profile
          </ButtonInternalLink>
        )}
      </PageHeader>
      <CardBody additionalStyles={additionalCardBodyStyling}>
        <DetailsRow leftText="Order id" rightText={order?.displayId} />
        <DetailsRow leftText="User" rightText={order?.customer.fullname} />
        <DetailsRow
          leftText="Phone number"
          rightText={order?.customer.phonenumber}
        />
        <DetailsRow leftText="Email" rightText={order?.customer.email} />
        <DetailsRow
          leftText="Order type"
          rightText={formatOrderType(order?.deliveryType!).orderType}
        />
        <DetailsRow leftText="Restaurant" rightText={order?.shop.name} />
        {order?.comment && (
          <DetailsRow leftText="Order comment" rightText={order.comment} />
        )}
        <DetailsRow
          leftText="Order source"
          rightText={displayOrderSource(order?.source)}
        />
        {order?.tableName && (
          <DetailsRow leftText="Eat-in" rightText={order.tableName} />
        )}
        {order?.buzzerNumber && (
          <DetailsRow leftText="Buzzer number" rightText={order.buzzerNumber} />
        )}
        {order?.onlinePosTawReference && (
          <DetailsRow
            leftText="Online POS TAW reference"
            rightText={order.onlinePosTawReference}
          />
        )}
        {order?.simphonyPosReference && (
          <DetailsRow
            leftText="Simphony POS reference"
            rightText={order.simphonyPosReference}
          />
        )}
        {order?.deliverectReference && (
          <DetailsRow
            leftText="Deliverect reference"
            rightText={order.deliverectReference}
          />
        )}

        {order?.companyDetails?.eanNumber && (
          <DetailsRow
            leftText="EAN number"
            rightText={order?.companyDetails?.eanNumber}
          />
        )}
        {order?.companyDetails?.name && (
          <DetailsRow
            leftText="Company name"
            rightText={order?.companyDetails?.name}
          />
        )}
        {order?.companyDetails?.phoneNumber && (
          <DetailsRow
            leftText="Company phone number"
            rightText={order?.companyDetails?.phoneNumber}
          />
        )}
        {order?.companyDetails?.vatNumber && (
          <DetailsRow
            leftText="VAT number"
            rightText={order?.companyDetails?.vatNumber}
          />
        )}
        {order?.invoiceAddress?.addressLine && (
          <DetailsRow
            leftText="Invoice address"
            rightText={
              order?.invoiceAddress?.addressLine +
              (order?.invoiceAddress?.addressLine2
                ? ", " + order?.invoiceAddress?.addressLine2
                : "")
            }
          />
        )}
        {order?.invoiceAddress?.city && (
          <DetailsRow
            leftText="Invoice city"
            rightText={order?.invoiceAddress?.city}
          />
        )}
        {order?.invoiceAddress?.postcode && (
          <DetailsRow
            leftText="Invoice ZIP code"
            rightText={order?.invoiceAddress?.postcode}
          />
        )}
        {order?.attention && (
          <DetailsRow leftText="Attention" rightText={order?.attention} />
        )}
        {order?.isInDeliveryForced && (
          <StyledInfoBox
            title="OBS"
            subtitle='This order was forced into status "In delivery"'
          />
        )}
        {order?.isDeliveredForced && (
          <StyledInfoBox
            title="OBS"
            subtitle='This order was forced into status "Delivered"'
          />
        )}
        {order?.paymentMethod === "NETS" && (
          <StyledInfoBox
            title="NETS payments cannot be refunded from admin"
            subtitle="Refunding transactions is not supported by NETS. Instead issue a refund via your point of sale system or contact HeapsGo support."
          />
        )}
      </CardBody>
    </CardContainer>
  );
};
